<template>
    <div class="www pt-2 pb-6 select-none">
        <div class="flex items-center justify-start gap-1 pt-5">
            <ActionButton
                :value="t('templatePreview')"
                state="promo"
                :icon="MdiFileDocumentCheck"
                :is-disabled="!templateVersionModel.templateVersionId"
                @click="
                    if (templateVersionModel.templateVersionId) {
                        ask();
                    }
                "
            />
            <ActionButton
                v-if="templateModel.type == 'PDF' && templateVersionModel.templateVersionId !== '0'"
                :value="t('buttonValidate')"
                state="success"
                :icon="MdiTestTube"
                :is-disabled="
                    !templateVersionModel.templateVersionId || !templateVersionModel.isPdfUaStrict
                "
                @click="
                    if (templateVersionModel.templateVersionId) {
                        validate();
                    }
                "
            />
            <ActionButton
                :value="t('buttonSchema')"
                state="info"
                :icon="MdiApplicationBrackets"
                :is-disabled="!templateVersionModel.templateVersionId"
                @click="
                    if (templateVersionModel.templateVersionId) {
                        schema();
                    }
                "
            />
            <ActionButton
                v-if="!templateVersionModel.templateVersionId"
                :value="t('buttonImport')"
                state="warning"
                :icon="MdiDatabaseImport"
                @click="dropzone.click()"
            />
            <ActionButton
                v-if="templateVersionModel.templateVersionId"
                :value="t('buttonExport')"
                state="info"
                :icon="MdiDatabaseExport"
                :is-disabled="!templateVersionModel.templateVersionId"
                @click="downloadTemplateVerisonData()"
            />
            <input
                id="dropzone-file"
                ref="dropzone"
                type="file"
                class="hidden"
                accept="application/json"
                @change="uploadTemplateVerisonData()"
            />
        </div>
    </div>

    <TemplateSchemaModal
        :state="schemaModal.state"
        :template-model="previewModal.templateModel"
        :template-version-model="previewModal.templateVersionModel"
        :used-placeholders="previewModal.usedPlaceholders"
    />

    <TemplatePreviewModal
        :state="previewModal.state"
        :template-model="previewModal.templateModel"
        :template-version-model="previewModal.templateVersionModel"
        :used-placeholders="previewModal.usedPlaceholders"
    />

    <TemplateValidationModal
        :state="validationModal.state"
        :template-version-id="templateVersionModel.templateVersionId"
    />
</template>

<script setup>
import { reactive, ref } from 'vue';
import { t } from '../composables/i18n';
import slugify from 'slugify';
import { exportTemplateVersion, importTemplateVersion } from '../composables/useImportExport';
import { notify } from 'notiwind';
import { harvestTemplate } from '../composables/useFreemarker';
import {
    MdiFileDocumentCheck,
    MdiApplicationBrackets,
    MdiDatabaseExport,
    MdiDatabaseImport,
    MdiTestTube,
} from 'materialdesignicons-vue3/icons/';
import TemplatePreviewModal from '../components/TemplatePreviewModal.vue';
import TemplateSchemaModal from '../components/TemplateSchemaModal.vue';
import TemplateValidationModal from '../components/TemplateValidationModal.vue';
import ActionButton from '../ui/ActionButton.vue';
import { cloneData } from '../composables/utils';

defineExpose({
    name: 'BlockDeleteWidget',
});

const props = defineProps({
    templateVersionModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    templateModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    templateEditModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
});

const templateVersionModel = props.templateVersionModel;
const templateModel = props.templateModel;
const templateEditModel = props.templateEditModel;
const dropzone = ref(null);

const usedPlaceholdersExport = reactive([]);

const validate = async () => {
    validationModal.state.open = true;
};

const refreshUsedPlaceholders = async () => {
    usedPlaceholdersExport.splice(0, usedPlaceholdersExport.length);
    await harvestTemplate(templateVersionModel.source, usedPlaceholdersExport);
};

const schema = async () => {
    await refreshUsedPlaceholders();
    schemaModal.state.open = true;
};

const ask = async () => {
    await refreshUsedPlaceholders();
    previewModal.state.open = true;
};

// Import
const uploadTemplateVerisonData = async (fileInputId = '#dropzone-file') => {
    const input = document.querySelector(fileInputId).files[0];
    const readFile = new FileReader();
    readFile.onload = (e) => {
        const contents = e.target.result;
        try {
            const model = JSON.parse(contents);
            importTemplateVersion(templateVersionModel, templateEditModel, model);
            console.log('Update', templateEditModel);
        } catch (e) {
            // alert here
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: `Parse error ${input.name}: ${e.message}`,
                },
                5000,
            );
        }
    };
    readFile.readAsText(input);
};

//Export
const downloadTemplateVerisonData = () => {
    const templateVersion = exportTemplateVersion(cloneData(templateVersionModel));
    const blob = new Blob([JSON.stringify(templateVersion, null, 4)], {
        type: 'application/json',
    });
    const linkSource = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = `TEMPLATING-EXPORT-VERSION-${slugify(templateModel.code)}-${templateVersionModel.langCode}-v${
        templateVersionModel.version
    }.json`;
    downloadLink.click();
};

const schemaModal = reactive({
    state: {
        open: false,
        title: t('buttonSchema'),
        text: t('templateSchemaDescription'),
        maxWidthMax: false,
        data: {},
    },
    buttons: [],
    icon: MdiApplicationBrackets,
});

const previewModal = reactive({
    state: {
        open: false,
        title: t('templatePreview'),
        text: t('templatePreviewDescription'),
        data: {},
        maxWidthMax: true,
        overlayPreventClose: true,
    },
    templateVersionModel: templateVersionModel,
    templateModel: templateModel,
    usedPlaceholders: usedPlaceholdersExport,
});

const validationModal = reactive({
    state: {
        open: false,
        title: t('buttonValidate'),
        text: t('templateValidationDescription'),
        data: {},
        maxWidthMax: true,
        overlayPreventClose: true,
    },
    result: {},
});
</script>
